import { useEffect, useRef, useState, useCallback } from 'react';
import { VerticalScrollerCore } from './VerticalScroller.core';
import { UseVerticalScrollerOptions, Pane } from './types';

export function useVerticalScroller(
  panes: Pane[],
  options: UseVerticalScrollerOptions = {}
) {
  const {
    bufferSize = 3,
    preloadImages = true,
    onPaneChange,
    // animationDuration = 300, // Reserved for future use
  } = options;

  const containerRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<VerticalScrollerCore | null>(null);
  const [currentPaneId, setCurrentPaneId] = useState<string>(
    panes[0]?.id || ''
  );
  const [visiblePanes, setVisiblePanes] = useState<Pane[]>([]);

  // Calculate which panes should be visible based on current position
  const updateVisiblePanes = useCallback((focusedId: string) => {
    const currentIndex = panes.findIndex((p) => p.id === focusedId);
    if (currentIndex === -1) return;

    const startIndex = Math.max(0, currentIndex - bufferSize);
    const endIndex = Math.min(panes.length - 1, currentIndex + bufferSize);
    
    const newVisiblePanes = panes.slice(startIndex, endIndex + 1);
    setVisiblePanes(newVisiblePanes);

    // Preload images in the buffer zone
    if (preloadImages) {
      newVisiblePanes.forEach((pane) => {
        if (pane.preload !== false) {
          preloadPaneImages(pane);
        }
      });
    }
  }, [panes, bufferSize, preloadImages]);

  // Preload images in a pane
  const preloadPaneImages = (pane: Pane) => {
    if (!pane.content) return;

    // Extract image URLs from the content (this is a simplified version)
    const container = document.createElement('div');
    container.innerHTML = String(pane.content);
    const images = container.querySelectorAll('img');
    
    images.forEach((img) => {
      const src = img.getAttribute('src') || img.getAttribute('data-src');
      if (src) {
        const image = new Image();
        image.src = src;
      }
    });
  };

  // Initialize the scroller
  useEffect(() => {
    if (!containerRef.current || panes.length === 0) return;

    const scroller = new VerticalScrollerCore(
      containerRef.current,
      panes[0]?.id || ''
    );

    scrollerRef.current = scroller;

    // Listen for pane changes
    scroller.on('set', (id: string) => {
      setCurrentPaneId(id);
      updateVisiblePanes(id);
      
      const index = panes.findIndex((p) => p.id === id);
      onPaneChange?.(id, index);
    });

    // Initial visible panes
    updateVisiblePanes(panes[0]?.id || '');

    return () => {
      scroller.destroy();
    };
  }, [panes, updateVisiblePanes, onPaneChange]);

  // Programmatically scroll to a pane
  const scrollToPane = useCallback((paneId: string) => {
    if (scrollerRef.current) {
      scrollerRef.current.setPane(paneId);
    }
  }, []);

  // Get current pane index
  const currentPaneIndex = panes.findIndex((p) => p.id === currentPaneId);

  return {
    containerRef,
    currentPaneId,
    currentPaneIndex,
    visiblePanes,
    scrollToPane,
    totalPanes: panes.length,
  };
}